import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { Avatar, FetchAPI, FormAPI, LegendsAPI, PanelAPI, ToastNotificationAPI, UnboxedAutocompleteAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationQualificationsAliases as Aliases, FragmentNames  } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

interface Qualification {
  dsAccreditation: string,
  dsAccreditationType: string,
  dsAccreditationLevel: string,
  nrSeqQualification: number,
  ieAccreditationLevel: string,
  nrSeqAccreditationInstitution: number,
  ieAccreditation: string,
  ieAction: ActionTypes,
  dsAction: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};
const ACCREDITATION_QUALIFICATION = 'AONA';

@Controller({ fragment: FragmentNames.ACCREDITATION_QUALIFICATIONS })
class AccreditationQualificationsController extends BaseController {
  @Inject(Aliases.FETCH_QUALIFICATIONS)
  private fetchQualifications: FetchAPI;
  @Inject(Aliases.FORM_QUALIFICATION)
  private formQualification: FormAPI;
  @Inject(Aliases.FETCH_FORM_QUALIFICATION)
  private fetchFormQualification: FetchAPI;
  @Inject(Aliases.UNBOXED_AUTOCOMPLETE_LEVEL)
  private autocompleteAccreditationLevel: UnboxedAutocompleteAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotificationPendencies: ToastNotificationAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnAfterFetch(Aliases.FETCH_QUALIFICATIONS)
  onAfterFetchQualifications(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnMount(Aliases.PANEL_FORM)
  onMountAddButtom(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.QUALIFICATIONS);
  }

  @OnMount(Aliases.PANEL_ACCREDITATION_QUALIFICATIONS)
  onMountMainPanel(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    this.context.set({ nrSeqProvider, nrSeqAccreditation });
  }

  @OnChange(Aliases.UNBOXED_AUTOCOMPLETE_ACCREDITATION)
  onchangeAutocomplete(event: ChangeEvent<string>): void {
    if (event.value === ACCREDITATION_QUALIFICATION) {
      this.autocompleteAccreditationLevel.setEnabled(true);
    } else {
      this.autocompleteAccreditationLevel.setEnabled(false);
      this.autocompleteAccreditationLevel.setValue(undefined);
    }
  }

  @OnSubmit(Aliases.FORM_QUALIFICATION)
  onSubmit(event: FormAPI): void {
    const body = event.record;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.post(Endpoint.POST.QUALIFICATION, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.cleanForm();
        this.fetchQualifications.forceFetch();
      }).catch((error) => {
        const { response } = error.body;
        ToastService.open({ message: response, level: ToastLevel.ERROR });
      });
  }

  @OnCancel(Aliases.FORM_QUALIFICATION)
  onCancelForm(): void {
    this.cleanForm();
  }

  private cleanForm(): void {
    this.formQualification.setRecord({ });
    this.formQualification.setData({ });
    this.formQualification.setValue({ });
    this.autocompleteAccreditationLevel.setEnabled(false);
    this.autocompleteAccreditationLevel.setValue(undefined);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.QUALIFICATIONS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetchPendencies(event: FetchEvent): void {
    if(isEmptyArrayOrObject(event.data)){
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastNotificationPendencies, event);
    }
  }

  @OnDynamicContentRender(Aliases.AVATAR)
  onMountCard(event: RenderEvent): void {
    const { render } = event;
    const { dsAccreditation, dsAccreditationType, dsAccreditationLevel } = event.data as Qualification;
    const level = dsAccreditationLevel ? `${MessageService.translate('provider.level')} ${dsAccreditationLevel}` : '';
    render((): ReactElement =>
      <Avatar
        hideImage={true}
        title={dsAccreditation}
        subtitle={dsAccreditationType}
        description={level}
      />
    );
  }

  @HPMSOnCreateMenu(Aliases.CARD_QUALIFICATION)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnAfterFetch(Aliases.FETCH_FORM_QUALIFICATION)
  onAfterFetchFormQualification(event: FetchEvent): void {
    if (event.data) {
      const { ieAccreditationLevel, ieAccreditation } = event.data as Qualification;
      if (ieAccreditationLevel && ieAccreditation === ACCREDITATION_QUALIFICATION) {
        this.autocompleteAccreditationLevel.setEnabled(true);
      }
    }
  }

  @HPMSOnCreateOption(Aliases.CARD_QUALIFICATION)
  onCreateOptionCustomCard(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;

    const show = {
      [OptionKey.EDIT]: editable == 'S',
      [OptionKey.DELETE]: editable == 'S',
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.CARD_QUALIFICATION)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, any>>): void {
    const { data, key } = event;
    const { nrSeqQualification } = data as Qualification;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(nrSeqQualification),
      [OptionKey.DELETE]: () => this.deleteRecord(nrSeqQualification),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private editRecord(nrSeqQualification: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.fetchFormQualification.setParameters({ nrSeqProvider, nrSeqAccreditation, nrSeqQualification });
    this.fetchFormQualification.forceFetch();
  }

  private deleteRecord(nrSeqQualification: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.delete(Endpoint.DELETE.QUALIFICATION, { nrSeqProvider, nrSeqAccreditation, nrSeqQualification })
      .then(() => {
        this.fetchQualifications.forceFetch();
      }).catch((response) => ToastService.open(response));
  }
}

export {
  AccreditationQualificationsController,
};
